import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import { NavigationService } from '../services/navigation.service';
import { GlobalService } from '../services/global.service';

import { UserService } from '../services/user.service';
import { ClientService } from '../services/client.service';

import { CLIENT_ID, ROUTES } from '../constants/navigation.constants';
import { MAX_LENGTH_ENTITY } from 'constants/limite.constants';

import { Client, CommonGlobalService, CommonInfoService, MultiSelect, Option, RootState, UserFilter, UserLinked, CommonCloudService, CommonSiteService, CommonDocumentService } from '@umanforge/common';
import type { ClientCloudInfo, SiteCloudInfo, Site } from '@umanforge/common';
import { ModifyClient } from 'interfaces/api_client.interfaces';

import { HomeTopBar } from '../components/menu/home_top_bar.component';
import { Breadcrumb } from '../components/menu/breadcrumb.component';
import { Button } from '../components/inputs/button.component';
import { Input } from '../components/inputs/input.component';
import { Loading } from '../components/loading.component';
import { SwitchAdminUser } from 'components/users/switch_admin_user.component';
import { ConfirmPasswordModal } from 'components/modals/confirm_password_modal.component';
import { HomeLeftBar } from 'components/menu/home_left_bar.component';

import { UsersIcon } from '../components/icons/users_icon.component';
import { ClientsIcon } from '../components/icons/clients_icon.component';
import { SaveIcon } from '../components/icons/save_icon.component';
import { BinIcon } from '../components/icons/bin_icon.component';
import { ToastService } from 'services/toast.service';
import { SitesIcon } from 'components/icons/sites_icon.component';
import { SectorsIcon } from 'components/icons/sectors_icon.component';
import { StorageIcon } from 'components/icons/storage.icon';
import { StorageSummary } from 'components/sites/storage_summary.component';
import { StorageAllocationList, StorageAllocationItem } from 'components/sites/storage_allocation_list.component';

const toastService: ToastService = new ToastService( 'Details Client' );
const defaultChangedUsers = { added: [], removed: [] };


export function DetailsClientsView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();

  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );
  const dispatch = useDispatch();

  /*----------------------------- useState ------------------------------*/
  const [ client, setClient ] = useState<Client | null>( null );

  const [ changedUsers, setChangedUsers ] = useState<{ added: UserLinked[], removed: UserLinked[] }>( defaultChangedUsers );
  const [ defaultUsers, setDefaultUsers ] = useState<Option<UserLinked>[]>( [] );
  const [ selectedAdmins, setSelectedAdmins ] = useState<Option<UserLinked>[]>( [] );
  const [ modifyClient, setModifyClient ] = useState<ModifyClient>( {} );

  const [ canPut, setCanPut ] = useState<boolean>( false );
  const [ hasChanged, setHasChanged ] = useState<boolean>( false );
  const [ isAdminOfClient, setIsAdminOfClient ] = useState<boolean>( false );
  const [ confirmDeleteModalIsOpen, setConfirmDeleteModalIsOpen ] = useState( false );

  const [ isLoading, setIsLoading ] = useState<boolean>( false );
  const [ storageItems, setStorageItems ] = useState<StorageAllocationItem[]>( [] );
  const [ defaultStorageQuotas, setDefaultStorageQuotas ] = useState<Map<string, number>>( new Map() );
  const [ defaultStorageAlerts, setDefaultStorageAlerts ] = useState<Map<string, { alertEnabled: boolean; alertThreshold?: number | null }>>( new Map() );
  const [ clientCloudInfo, setClientCloudInfo ] = useState<ClientCloudInfo | null>( null );

  const computeTotalAllocatedKo = () => {
    const factor = 1048576; // 1024 * 1024
    return storageItems
      .reduce( ( sum, item ) => { const v = Number( item.quota ?? 0 ); return sum + ( Number.isFinite( v ) ? Math.round( v * factor ) : 0 ); }, 0 );
  };

  /*---------------------------- useEffect ------------------------------*/
  useEffect( () => {
    if ( me ) {
      refreshClient();
    }
  }, [ me ] );

  useEffect( () => {
    if ( client ) {
      checkCanPut();
    }
  }, [ modifyClient, changedUsers, storageItems ] );

  useEffect( () => {
    if ( client ) {
      const clientQuotaKo = client.maxCloudSizeKo ?? 0;
      const siteItems: StorageAllocationItem[] = ( client.sites ?? [] ).map( site => {
        const siteMaxKo = site.maxCloudSizeKo ?? 0;
        const siteUsedKo = 'usedSizeKo' in site ? ( ( site as unknown as SiteCloudInfo ).usedSizeKo ?? 0 ) : 0;
        return {
          id: site.id,
          icon: <SitesIcon className='w-5 h-5' />,
          title: `${ t( 'common:site' ) } : ${ site.name.toUpperCase() }`,
          maxQuota: clientQuotaKo,
          used: siteUsedKo,
          quota: siteMaxKo / 1048576,
          alertEnabled: false,
          alertThreshold: 90,
          disabled: !isAdminOfClient,
        };
      } );
      const systemItem: StorageAllocationItem = {
        id: 'system',
        icon: (
          <StorageIcon className='w-5 h-5 fill-current' />
        ),
        title: t( 'translation:system_storage' ),
        maxQuota: clientQuotaKo,
        used: 0,
        quota: 0,
        alertEnabled: false,
        alertThreshold: 90,
        disabled: !isAdminOfClient,
      };
      setStorageItems( [ systemItem, ...siteItems ] );
      const quotas = new Map<string, number>();
      const alerts = new Map<string, { alertEnabled: boolean; alertThreshold?: number | null }>();
      ( client.sites ?? [] ).forEach( site => { quotas.set( site.id, ( ( site.maxCloudSizeKo ?? 0 ) / 1048576 ) ); alerts.set( site.id, { alertEnabled: false, alertThreshold: 90 } ); } );
      quotas.set( 'system', 0 );
      alerts.set( 'system', { alertEnabled: false, alertThreshold: 90 } );
      setDefaultStorageQuotas( quotas );
      setDefaultStorageAlerts( alerts );
    }
  }, [ client, isAdminOfClient ] );

  useEffect( () => {
    const items = {
      added: selectedAdmins
        .map( user => { return user.object; } )
        .filter( user => {
          return (
            ( !defaultUsers.find( clientUser => { return ( clientUser.object.id === user.id ); } ) && user.isAdmin === true ) ||
            ( defaultUsers.find( clientUser => { return ( clientUser.object.id === user.id && ( clientUser.object.isAdmin === false || clientUser.object.isAdmin == null ) ) && user.isAdmin === true; } ) )
          );
        } ),
      removed: defaultUsers
        .map( clientUser => { return clientUser.object; } )
        .filter( clientUser => {
          return !selectedAdmins.find( user => { return ( user.object.id === clientUser.id ); } ) && clientUser.isAdmin === true;
        } )
    };

    setChangedUsers( items );
  }, [ selectedAdmins ] );

  /*------------------------- Requetes API User -------------------------*/
  const refreshClient = async () => {
    setClient( null );
    setIsLoading( true );
    if ( params.clientId ) {
      const refreshedClient = await ClientService.getClient( params.clientId, token, dispatch, () => {
        NavigationService.toClients( navigate, false );
      } );
      let isAdmin = false;
      if ( me ) {
        isAdmin = CommonGlobalService.isAdminOfClient( me, refreshedClient.id );
      }
      setIsAdminOfClient( isAdmin );
      Promise.all( [
        UserService.getUsersOfClient( params.clientId, token, dispatch, () => { NavigationService.toClients( navigate, false ); } ),
        UserService.getAdminsOfClient( params.clientId, token, dispatch, () => { NavigationService.toClients( navigate, false ); } ),
      ] )
        .then( responses => {
          const admins = ( responses[ 1 ] ?? [] ).map( user => { return GlobalService.toUserLinked( user, true ); } );
          const users = ( responses[ 0 ] ?? [] ).map( user => { return GlobalService.toUserLinked( user, false ); } )
            .filter( user => { return admins.find( admin => { return user.id === admin.id; } ) == null; } );

          setDefaultUsers(
            CommonGlobalService.toOptions( admins.concat( users ), isAdmin, !isAdmin )
              .sort( ( user1, user2 ) => { return user1.object.isAdmin > user2.object.isAdmin ? -1 : 1; } )
          );
          setSelectedAdmins(
            CommonGlobalService.toOptions( admins, isAdmin && admins.length > 1, !isAdmin )
              .sort( ( user1, user2 ) => { return user1.object.isAdmin > user2.object.isAdmin ? -1 : 1; } )
          );
          setClient( refreshedClient );
          ( async () => {
            const info: ClientCloudInfo | null = await CommonCloudService.getClientCloudInfo( refreshedClient.id, token, dispatch );
            setClientCloudInfo( info );
            const clientQuotaKo = info?.maxSizeKo ?? ( refreshedClient.maxCloudSizeKo ?? 0 );
            const sitesSource: Array<SiteCloudInfo | Site> = info?.sites ?? refreshedClient.sites ?? [];
            const siteItems: StorageAllocationItem[] = ( sitesSource ?? [] ).map( site => {
              const siteMaxKo = 'maxSizeKo' in site ? ( site.maxSizeKo ?? 0 ) : ( site.maxCloudSizeKo ?? 0 );
              const siteUsedKo = 'usedSizeKo' in site ? ( site.usedSizeKo ?? 0 ) : 0;
              const siteAlert = 'alertThresholdInPerCent' in site ? ( site.alertThresholdInPerCent ) : undefined;
              const rawId = 'id' in site ? site.id : null;
              const id = rawId ?? 'system';
              const isSystem = id === 'system';
              const nameFromSite = !isSystem && 'name' in site ? ( ( site as Site ).name ?? '' ) : '';
              const refreshedSites = refreshedClient.sites ?? [];
              // eslint-disable-next-line max-nested-callbacks
              const matchedSite = !isSystem ? refreshedSites.find( s => {return s.id === rawId;} ) : undefined;
              const nameFallback = matchedSite?.name ?? '';
              const name = nameFromSite || nameFallback || '';
              return {
                id: id,
                icon: isSystem ? (
                  <StorageIcon className='w-5 h-5 fill-current' />
                ) : <SitesIcon className='w-5 h-5' />,
                title: isSystem ? t( 'translation:system_storage' ) :  `${ t( 'common:site' ) } : ${ name.toUpperCase() }`,
                maxQuota: clientQuotaKo,
                used: siteUsedKo,
                quota: siteMaxKo / 1048576,
                alertEnabled: siteAlert !== undefined,
                alertThreshold: siteAlert ?? 90,
                disabled: !isAdmin,
              };
            } );
            const apiSystem = siteItems.find( s => {return s.id === 'system';} );
            const systemItem: StorageAllocationItem = apiSystem ?? {
              id: 'system',
              icon: (
                <StorageIcon className='w-5 h-5 fill-current' />
              ),
              title: t( 'translation:system_storage' ),
              maxQuota: clientQuotaKo,
              used: 0,
              quota: 0,
              alertEnabled: false,
              alertThreshold: 90,
              disabled: !isAdmin,
            };
            const finalItems = [ systemItem, ...siteItems.filter( s => {return s.id !== 'system';} ) ];
            setStorageItems( finalItems );
            const quotas = new Map<string, number>();
            const alerts = new Map<string, { alertEnabled: boolean; alertThreshold?: number | null }>();
            finalItems.forEach( s => {
              quotas.set( s.id, Number( s.quota ?? 0 ) );
              alerts.set( s.id, { alertEnabled: !!s.alertEnabled, alertThreshold: s.alertThreshold ?? null } );
            } );
            setDefaultStorageQuotas( quotas );
            setDefaultStorageAlerts( alerts );
          } )();
        } );
      setIsLoading( false );
    }
  };

  const deleteClient = ( password: string ): Promise<boolean> => {
    return new Promise ( async resolve => {
      setIsLoading( true );
      let isDeleted = false;
      if ( params.clientId ) {
        const seed = await CommonInfoService.getAuthInfo( token, dispatch );
        if ( seed ) {
          isDeleted = await ClientService.deleteClient( params.clientId, password, seed, token, dispatch, refreshClient, refreshClient );
        }
      }
      if ( isDeleted ) {
        toastService.showInfo( t( 'translation:deleted' ).replace( '{0}', ( client?.name ?? t( 'translation:the_client' ) ) ) );
        NavigationService.toClients( navigate, false );
      }
      setIsLoading( false );
      resolve( isDeleted );
    } );
  };

  const putClient = (): Promise<boolean> => {
    return new Promise ( async resolve => {
      let isUpdated = false;
      const isClientChange: boolean =  modifyClient.name != null && modifyClient.name != '' && modifyClient.name != client?.name;
      if ( params.clientId && isClientChange ) {
        isUpdated = await ClientService.putClient( params.clientId, modifyClient, token, dispatch, refreshClient, refreshClient );
      }
      resolve( isUpdated );
    } );
  };

  const allocateCloudStorage = (): Promise<boolean> => {
    return new Promise( async resolve => {
      // Only consider items that actually changed compared to defaults
      const changedItems = storageItems.filter( item => {
        const defaultQuota = defaultStorageQuotas.get( item.id ) ?? 0;
        if ( defaultQuota !== item.quota ) { return true; }
        const defAlert = defaultStorageAlerts.get( item.id );
        const defEnabled = defAlert ? !!defAlert.alertEnabled : false;
        const defThreshold = defAlert ? ( defAlert.alertThreshold ?? null ) : null;
        const curEnabled = !!item.alertEnabled;
        const curThreshold = curEnabled ? ( item.alertThreshold ?? null ) : null;
        if ( defEnabled !== curEnabled ) { return true; }
        if ( ( defThreshold ?? null ) !== ( curThreshold ?? null ) ) { return true; }
        return false;
      } );

      if ( changedItems.length === 0 ) {
        resolve( false );
        return;
      }

      const clientQuotaKo = clientCloudInfo ? ( clientCloudInfo.maxSizeKo ?? 0 ) : ( client?.maxCloudSizeKo ?? 0 );
      const totalAllocatedKo = storageItems
        .reduce( ( sum, item ) => { const v = Number( item.quota ?? 0 ); return sum + Math.round( ( Number.isFinite( v ) ? v : 0 ) * 1048576 ); }, 0 );
      if ( totalAllocatedKo > clientQuotaKo ) {
        const totalReadable = CommonDocumentService.getReadableFileSizeString( totalAllocatedKo );
        const quotaReadable = CommonDocumentService.getReadableFileSizeString( clientQuotaKo );
        const msg = t( 'translation:storage_total_exceeds_quota' )?.replace( '{0}', String( totalReadable ) ).replace( '{1}', String( quotaReadable ) ) || `Total allocations (${ totalReadable }) exceed client quota (${ quotaReadable })`;
        toastService.showError( msg );
        resolve( false );
        return;
      }

      const cloudStorages = changedItems
        .map( item => {
          const siteId = item.id === 'system' ? null : item.id;
          const allocatedSpaceKo = Math.round( Number( item.quota ?? 0 ) * 1048576 );
          const body: { siteId: string | null, allocatedSpace?: number, alertThresholdInPerCent?: number | null } = { siteId };
          if ( ( item.quota ?? 0 ) > 0 ) { body.allocatedSpace = allocatedSpaceKo; }
          body.alertThresholdInPerCent = item.alertEnabled ? ( item.alertThreshold ?? null ) : null;
          return body;
        } );
      const isUpdated = await CommonSiteService.putAllocatingCloudStorageForClient(
        client?.id ?? '',
        { cloudStorages: cloudStorages as unknown as { siteId: string | null; allocatedSpace?: number; alertThresholdInPerCent?: number | undefined }[] },
        token,
        dispatch
      );
      if ( !isUpdated ) {
        toastService.showError( t( 'translation:allocation_failed' ) || 'Failed to allocate storage' );
      }
      resolve( isUpdated );
    } );
  };

  const addClientToAdmins = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      if ( client?.id && changedUsers.added.length > 0 ) {
        const body = { admins: changedUsers.added.map( admin => { return admin.id; } ) };
        isUpdated = await ClientService.postClientToAdmins( client?.id, body, token, dispatch, refreshClient, refreshClient );
      }
      resolve( isUpdated );
    } );
  };

  const removedClientToAdmins = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      if ( client?.id && changedUsers.removed.length > 0 ) {
        const body = { admins: changedUsers.removed.map( admin => { return admin.id; } ) };
        isUpdated = await ClientService.deleteClientToAdmins( client?.id, body, token, dispatch, refreshClient, refreshClient );
      }
      resolve( isUpdated );
    } );
  };

  const getUsers = useCallback( async ( filter: UserFilter ): Promise<{ total: number, list: Option<UserLinked>[] }> => {
    const localFilter = { ...( filter || {} ) } as UserFilter;
    if ( localFilter.filterName === undefined || localFilter.filterName === '' ) {
      delete localFilter.filterName;
    }
    localFilter.filterClients = [ client?.id ?? '' ];
    const result = ( await UserService.getAllowedUsers( localFilter as UserFilter, token, dispatch ) );
    const usersList = CommonGlobalService.toOptions<UserLinked>(
      result.users
        .map<UserLinked>( user => {
          return {
            id: user.id,
            fullname: user.fullname,
            isDisable: user.isDisable,
            isAdminOfUser: user.isAdminOfUser,
            isAdmin: selectedAdmins.find( selectedUser => { return selectedUser.value === user.id; } )?.object.isAdmin ?? false,
          };
        } )
    )
      .sort( ( user1, user2 ) => {
        return user1.object.fullname.toUpperCase() < user2.object.fullname.toUpperCase() ? -1 : 1;
      } );
    return { total: result.total, list: usersList };
  }, [ client?.id, selectedAdmins, token, dispatch ] );

  /*---------------------------- Functions ------------------------------*/
  const onSaveClick = async () => {
    let isUserModify = false;
    setIsLoading( true );
    const clientQuotaKoGuard = clientCloudInfo ? ( clientCloudInfo.maxSizeKo ?? 0 ) : ( client?.maxCloudSizeKo ?? 0 );
    const totalGuardKo = computeTotalAllocatedKo();
    if ( totalGuardKo > clientQuotaKoGuard ) {
      const totalReadable = CommonDocumentService.getReadableFileSizeString( totalGuardKo );
      const quotaReadable = CommonDocumentService.getReadableFileSizeString( clientQuotaKoGuard );
      const msg = t( 'translation:storage_total_exceeds_quota' )?.replace( '{0}', String( totalReadable ) ).replace( '{1}', String( quotaReadable ) ) || `Total allocations (${ totalReadable }) exceed client quota (${ quotaReadable })`;
      toastService.showError( msg );
      setIsLoading( false );
      return;
    }
    if ( client ) {
      const isPutClient = await putClient();
      const isAddClientToAdmins = await addClientToAdmins();
      const isRemovedClientToAdmins = await removedClientToAdmins();
      const isAllocateStorage = await allocateCloudStorage();
      isUserModify = isPutClient ||
        isAddClientToAdmins ||
        isRemovedClientToAdmins ||
        isAllocateStorage;
    }
    if ( isUserModify ) {
      toastService.showInfo( t( 'translation:modified' ).replace( '{0}', ( client?.name ?? t( 'translation:the_client' ) ) ) );
      refreshClient();
    }
    setIsLoading( false );
  };

  const checkCanPut = () => {
    const requirement = ( modifyClient.name != '' );
    const isClientChange: boolean =  modifyClient.name != null && modifyClient.name != client?.name;
    const isUsersChange: boolean = changedUsers.added.length > 0 || changedUsers.removed.length > 0;
    const isStorageChange: boolean = storageItems.some( item => {
      const defaultQuota = defaultStorageQuotas.get( item.id ) ?? 0;
      if ( defaultQuota !== item.quota ) { return true; }
      const defAlert = defaultStorageAlerts.get( item.id );
      const defEnabled = defAlert ? !!defAlert.alertEnabled : false;
      const defThreshold = defAlert ? ( defAlert.alertThreshold ?? null ) : null;
      const curEnabled = !!item.alertEnabled;
      const curThreshold = curEnabled ? ( item.alertThreshold ?? null ) : null;
      if ( defEnabled !== curEnabled ) { return true; }
      if ( ( defThreshold ?? null ) !== ( curThreshold ?? null ) ) { return true; }
      return false;
    } );

    const clientQuotaKo = clientCloudInfo ? ( clientCloudInfo.maxSizeKo ?? 0 ) : ( client?.maxCloudSizeKo ?? 0 );
    const totalAllocatedKo = computeTotalAllocatedKo();
    const isOver = totalAllocatedKo > clientQuotaKo;
    if ( isOver ) {
      setCanPut( false );
    } else {
      setCanPut(
        requirement && (
          isClientChange ||
          isUsersChange ||
          isStorageChange
        )
      );
    }

    setHasChanged(
      isClientChange ||
      isUsersChange ||
      isStorageChange
    );
  };

  const getCountOfUser = () => {
    const allUsers = new Array<string>()
      .concat( defaultUsers.map( user => { return user.object.id; } ) )
      .concat( changedUsers.added.map( user => { return user.id; } ) );

    return Array.from( new Set( allUsers ) ).length;
  };


  /*------------------------------- Front -------------------------------*/
  return (
    <>
      <HomeLeftBar items={ NavigationService.getNavBarItems( token, dispatch ) } needConfirmation={ hasChanged } />
      <div className='flex flex-col min-w-[350px] h-full w-full'>
        <HomeTopBar title={ t( 'translation:modify_client' ) } needConfirmationToNav={ hasChanged }/>
        <Breadcrumb
          needConfirmationToNav={ hasChanged }
          links={
            [
              { label: t( 'common:clients' ), route: ROUTES.CLIENTS, icon: ( <ClientsIcon className='h-[14px] select-none cursor-pointer'/> ) },
              { label: client ? client.name : t( 'translation:modify_client' ), route: ROUTES.CLIENT.replace( CLIENT_ID, params.clientId ?? '' ) }
            ]
          }
        />
        {
          !isLoading && client ?
            <div className='flex flex-col items-center justify-center'>
              <form className='relative flex flex-col gap-4 h-full w-full min-w-[400px] mb-12 items-center justify-center p-4 px-[10%]'>

                <div className='w-full'>
                  <Input
                    required
                    maxLength={ MAX_LENGTH_ENTITY.UF_CLIENT_NAME }
                    label={ t( 'translation:client_name' ) }
                    disabled={ !isAdminOfClient }
                    placeholder={ t( 'translation:client' ) + '...' }
                    icon={ <ClientsIcon className='w-7 h-7' /> }
                    value={ client.name ?? ''}
                    onChange={ newClientName => {
                      modifyClient.name = client?.name != newClientName ? newClientName : undefined;
                      setModifyClient( structuredClone( modifyClient ) );
                    } }
                  />
                </div>

                <div className='w-full'>
                  <MultiSelect
                    icon={ <SitesIcon className='w-7 h-7'/> }
                    label={ t( 'translation:site' ) }
                    disabled={ true }
                    placeholder={ t( 'translation:no_associated_sites' ) }
                    getValues={ CommonGlobalService.toOptions( client.sites ?? [], false, true ) }
                    selectedValues={ CommonGlobalService.toOptions( client.sites ?? [], false, true ) }
                    clearable={ false }
                  />
                </div>

                <div className='w-full'>
                  <MultiSelect
                    icon={ <SectorsIcon className='w-7 h-7'/> }
                    label={ t( 'translation:sector' ) }
                    disabled={ true }
                    placeholder={ t( 'translation:no_associated_sectors' ) }
                    getValues={ CommonGlobalService.toOptions( GlobalService.getClientSectors( client ), false, true ) }
                    selectedValues={ CommonGlobalService.toOptions( GlobalService.getClientSectors( client ), false, true ) }
                    clearable={ false }
                  />
                </div>

                <div className='flex flex-col w-full gap-2'>
                  <MultiSelect
                    label={ t( 'translation:administrators' ) + ' (' + selectedAdmins.length + ')' }
                    disabled={ !isAdminOfClient }
                    placeholder={ t( 'translation:no_associated_users' ) }
                    icon={ <UsersIcon className='w-7 h-7' /> }
                    clearable={ false }
                    getValues={ getUsers }
                    selectedValues={ selectedAdmins }
                    onChange={ options => {
                      options.map( user => {
                        user.canDelete = options.length !== 1;
                        user.object.isAdmin = true;
                        return user;
                      } );
                      setSelectedAdmins( options );
                    } }
                    extra={ selectedValue => {
                      return (
                        <SwitchAdminUser
                          objectId={ selectedValue.value }
                          isDisabled={ true }
                          defaultValue={ selectedValue.object.isAdmin }
                          onChange={ value => {
                            selectedValue.object.isAdmin = value;
                            const findedUser = selectedAdmins.find( item => { return selectedValue.object.id == item.value; } );
                            if ( findedUser ) {
                              findedUser.object.isAdmin = value;
                            }
                            setSelectedAdmins( structuredClone( selectedAdmins ) );
                          } }
                        />
                      );
                    } }
                  />
                  {
                    me && CommonGlobalService.isAdminOfClient( me, client.id ) ?
                      <div className='flex gap-1.5 px-1 w-full'>
                        { t( 'translation:total_users' ) }
                        <span className='font-medium underline underline-offset-2'>
                          { String( getCountOfUser() ) }
                        </span>
                      </div>
                      :
                      null
                  }
                </div>

                <div className='w-full flex flex-col gap-4 mt-4'>
                  {
                    ( () => {
                      const quotaKo = clientCloudInfo ? ( clientCloudInfo.maxSizeKo ?? 0 ) : ( client?.maxCloudSizeKo ?? 0 );
                      const totalUsedKo = clientCloudInfo ? ( clientCloudInfo.usedSizeKo ?? 0 ) : Math.round( storageItems.reduce( ( sum, item ) => { return sum + ( Number( item.used ?? 0 ) * 1024 * 1024 ); }, 0 ) );
                      const availableKo = clientCloudInfo ? ( clientCloudInfo.availableSizeKo ?? 0 ) : undefined;
                      return (
                        <StorageSummary
                          title={ t( 'translation:global_summary' ) }
                          quota={ quotaKo }
                          totalUsed={ totalUsedKo }
                          available={ availableKo }
                        />
                      );
                    } )()
                  }
                  <StorageAllocationList
                    title={ t( 'translation:detailed_configuration' ) }
                    items={ storageItems }
                    onItemChange={ updatedItem => {
                      setStorageItems( prev => {
                        const result = prev.map( i => {
                          return i.id === updatedItem.id
                            ? {
                              ...updatedItem,
                              quota: Number( updatedItem.quota ?? 0 )
                            }
                            : i;
                        } );

                        const clientQuotaKo =
                          updatedItem.maxQuota ??
                          prev.reduce(
                            ( max, item ) => { return Math.max( max, Number( item.maxQuota ?? 0 ) ); },
                            0
                          );

                        const totalAllocatedKo = result.reduce(
                          ( sum, item ) => { const v = Number( item.quota ?? 0 ); return sum + Math.round( ( Number.isFinite( v ) ? v : 0 ) * 1048576 ); },
                          0
                        );

                        if ( totalAllocatedKo > clientQuotaKo ) {
                          toastService.showError(
                            t( 'translation:storage_quota_exceeded' )
                          );
                        }

                        return result;
                      } );
                    } }
                  />
                </div>

                <div className='flex justify-center w-full h-14 mt-10'>
                  {
                    me?.isSuperAdmin ?
                      <div className='h-fit w-1/3 min-w-[160px] max-w-[250px]'>
                        <Button
                          label={ t( 'common:delete' ) }
                          icon={ <BinIcon className='h-[24px] w-[24px] stroke-white'/> }
                          color='#f87171'
                          colorHover='#ef4444'
                          onClick={ () => { setConfirmDeleteModalIsOpen( true ); } }
                        />
                      </div>
                      :
                      null
                  }
                  <div className='h-fit w-1/3 min-w-[160px] max-w-[250px]'>
                    <Button
                      label={ t( 'translation:save' ) }
                      disable={ ( () => {
                        const clientQuotaKoLoc = clientCloudInfo ? ( clientCloudInfo.maxSizeKo ?? 0 ) : ( client?.maxCloudSizeKo ?? 0 );
                        const totalKoLoc = computeTotalAllocatedKo();
                        return !canPut || totalKoLoc > clientQuotaKoLoc;
                      } )() }
                      icon={ <SaveIcon className='h-[23px] w-[23px] stroke-white'/> }
                      color='#0bb173'
                      colorHover='#088D5C'
                      onClick={ onSaveClick }
                    />
                  </div>
                </div>
              </form>
              {
                confirmDeleteModalIsOpen ?
                  <ConfirmPasswordModal
                    title={ t( 'translation:deletion_of' ).replace( '{0}', client.name )  }
                    onClose={ () => { setConfirmDeleteModalIsOpen( false ); } }
                    onConfirm={ deleteClient }
                    isOpen={ confirmDeleteModalIsOpen }
                  >
                    <div className='flex flex-col w-full h-full p-3'>
                      <div className='flex flex-col text-[14px] font-[475] text-gray-600'>
                        <p> { t( 'translation:deletion_confirm_message' ).replace( '{0}', client.name ) } </p>
                        <p> { t( 'translation:enter_password_confirm' ) } </p>
                      </div>
                    </div>
                  </ConfirmPasswordModal>
                  :
                  null
              }
            </div>
            :
            <Loading/>
        }
      </div>
    </>
  );

}